import React from 'react';
import ReactMarkdown from 'react-markdown';
import {
    Container,
    Group,
    Heading,
    ButtonGroup,
    Button,
    useBreakpoint,
} from '@landr/maestro';
import { t } from '@lingui/macro';
import { RouterLink } from 'components/CommonUi/RouterLink';
import { DownloadButton } from 'components/CommonUi/DownloadButton';
import MetasBadge from 'components/MetasBadge/MetasBadge';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { BaseMeta, Pack, RePack } from 'types';
import { PackState } from 'types/enums';
import { analytics } from 'utils/analytics';
import {
    CtaGoal,
    CtaType,
    InteractionSource,
} from 'utils/analytics/AnalyticsV2Service.generated';
import TextDecorationUnderline from 'components/TextDecorationUnderline/TextDecorationUnderline';
import useGetLocalizedEntityDetailsLink from 'hooks/useGetLocalizedEntityDetailsLink/useGetLocalizedEntityDetailsLink';
import { usePartner } from 'hooks/usePartner';
import { useToLocalizedPath } from 'hooks/useToLocalizedPath/useToLocalizedPath';
import { getViewCtaCopy } from '../helpers/getViewCtaCopy';
import { usePreviewContext } from '../PreviewContext';
import { PreviewContextContent, PreviewContextSamples } from '../types';
import PreviewDetailsSkeleton from './PreviewDetailsSkeleton';

const ProviderLink = styled(Link)`
    text-decoration: none;
`;

export const _PreviewDetails = ({
    title,
    description,
    metas,
    viewCtaCopy,
    viewCtaLink,
    downloadCtaCopy,
    entity,
    bylineSamplesTotal,
    bylineLabelName,
    partnerLink,
    showDownloadIconButton,
    handleViewClicked,
    handleDownloadClicked,
    isPurchaseButtonDisabled,
}: {
    title?: string;
    description?: string;
    metas?: BaseMeta<string>[];
    viewCtaCopy: string;
    viewCtaLink: string;
    downloadCtaCopy: string;
    entity: Pack | RePack;
    bylineSamplesTotal: string | undefined;
    bylineLabelName: string | undefined;
    partnerLink: string | undefined;
    showDownloadIconButton: boolean;
    handleViewClicked: () => void;
    handleDownloadClicked: () => void;
    isPurchaseButtonDisabled: boolean;
}) => (
    <Group.Item>
        <Container size="xs">
            <Group direction="vertical" gutter="sm">
                <Group.Item>
                    <Heading size="xl" as="h5" nbOfLines={2} mb={0}>
                        {title}
                    </Heading>
                </Group.Item>
                <Group.Item>
                    {bylineSamplesTotal}
                    {bylineSamplesTotal && bylineLabelName && ' · '}
                    {bylineLabelName && (
                        <ProviderLink to={partnerLink ?? ''}>
                            {bylineLabelName}
                        </ProviderLink>
                    )}
                </Group.Item>
                <Group.Item>
                    <MetasBadge
                        metas={metas}
                        variant="silver"
                        kind="subtle"
                        size="sm"
                        gutter="xs"
                    />
                </Group.Item>
                <Group.Item>
                    <TextDecorationUnderline size="sm" nbOfLines={2} mb="lg">
                        <ReactMarkdown
                            children={description ?? ''}
                            components={{ p: 'span' }}
                        />
                    </TextDecorationUnderline>
                </Group.Item>
                <Group.Item>
                    <ButtonGroup>
                        {showDownloadIconButton ? (
                            <DownloadButton
                                item={entity}
                                isRounded
                                onClick={handleDownloadClicked}
                                isDisabled={isPurchaseButtonDisabled}
                            />
                        ) : (
                            <DownloadButton
                                item={entity}
                                isRounded
                                showIcon={false}
                                onClick={handleDownloadClicked}
                                isDisabled={isPurchaseButtonDisabled}
                            >
                                <>{downloadCtaCopy}</>
                            </DownloadButton>
                        )}

                        <Button
                            as={RouterLink}
                            to={viewCtaLink}
                            variant="secondary"
                            isRounded
                            onClick={handleViewClicked}
                        >
                            {viewCtaCopy}
                        </Button>
                    </ButtonGroup>
                </Group.Item>
            </Group>
        </Container>
    </Group.Item>
);

export const usePreviewDetails = ({
    preview,
    samples,
    contentGroup,
    contentList,
}: {
    preview?: PreviewContextContent;
    samples?: PreviewContextSamples;
    contentGroup?: string;
    contentList?: string;
}) => {
    const getLocalizedEntityDetailsLink = useGetLocalizedEntityDetailsLink();

    const viewCtaLink =
        getLocalizedEntityDetailsLink(preview?.type, preview?.slug) ?? '';
    const viewCtaCopy = preview ? getViewCtaCopy(preview.type) : '';
    const downloadCtaCopy = preview ? t`Download` : '';
    const bylineSamplesTotal = samples?.total
        ? `${samples?.total} samples`
        : '';

    const bylineLabelName = preview?.labelName;

    const isPurchaseButtonDisabled =
        !preview?.entity?.isDownloaded &&
        preview?.entity?.packState === PackState.TakenDown;

    const handleDownloadClicked = () => {
        analytics.trackCTAClicked({
            ctaGoal: CtaGoal.DownloadSample,
            ctaType: CtaType.Button,
            interactionSource: InteractionSource.AssetAction,
            ctaText: preview?.entity.name,
            ctaContentGroup: contentGroup,
            ctaContentList: contentList,
        });
    };

    const handleViewClicked = () => {
        analytics.trackCTAClicked({
            ctaGoal: CtaGoal.ViewDetail,
            ctaType: CtaType.Button,
            interactionSource: InteractionSource.CardAction,
            ctaDestination: viewCtaLink,
            ctaText: viewCtaCopy,
            ctaContentGroup: contentGroup,
            ctaContentList: contentList,
        });
    };

    return {
        viewCtaLink,
        viewCtaCopy,
        downloadCtaCopy,
        bylineSamplesTotal,
        bylineLabelName,
        handleViewClicked,
        handleDownloadClicked,
        isPurchaseButtonDisabled,
    };
};

const PreviewDetails = () => {
    const { preview, samples, contentGroup, contentList } = usePreviewContext();

    const partner = usePartner(preview?.entity);
    const toLocalizedPath = useToLocalizedPath();

    const partnerLink = partner
        ? toLocalizedPath(`/${partner.route}/${partner.slug}`)
        : undefined;

    const showDownloadIconButton = useBreakpoint('lg');
    const {
        viewCtaLink,
        viewCtaCopy,
        downloadCtaCopy,
        bylineSamplesTotal,
        bylineLabelName,
        handleViewClicked,
        handleDownloadClicked,
        isPurchaseButtonDisabled,
    } = usePreviewDetails({
        preview,
        samples,
        contentGroup,
        contentList,
    });

    if (!preview || !preview?.slug) {
        return (
            <PreviewDetailsSkeleton
                showDownloadIconButton={showDownloadIconButton}
            />
        );
    }

    return (
        <_PreviewDetails
            entity={preview.entity}
            title={preview.title}
            description={preview.description}
            bylineSamplesTotal={bylineSamplesTotal}
            bylineLabelName={bylineLabelName}
            partnerLink={partnerLink}
            metas={preview.tags}
            viewCtaLink={viewCtaLink}
            viewCtaCopy={viewCtaCopy}
            downloadCtaCopy={downloadCtaCopy}
            showDownloadIconButton={showDownloadIconButton}
            handleViewClicked={handleViewClicked}
            handleDownloadClicked={handleDownloadClicked}
            isPurchaseButtonDisabled={isPurchaseButtonDisabled}
        />
    );
};

export default PreviewDetails;
